// Helpers puros para el selector de destinatarios del correo: unen las fuentes de
// contactos guardados (proveedores + contactos del cliente del caso + asesores +
// equipo interno) en una sola lista y la ordenan por coincidencia al escribir.
import type { Asesor, Contacto, Proveedor, UserMini } from "@/lib/api";

export type FuenteContacto = "proveedor" | "cliente" | "asesor" | "interno";

export type ContactoUnificado = {
  nombre: string;
  email: string;
  fuente: FuenteContacto;
  etiqueta: string; // proveedor/empresa o "Asesor comercial"
  esPrincipal: boolean;
  key: string;
};

const norm = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

/**
 * Une contactos de proveedores + contactos del cliente del caso + asesores en una
 * sola lista con email válido, deduplicada por email (case-insensitive; la primera
 * fuente en orden proveedor→cliente→asesor gana).
 */
export function construirContactosUnificados(
  proveedores: Proveedor[],
  contactosCliente: Contacto[],
  asesores: Asesor[],
  usuarios: UserMini[] = [],
): ContactoUnificado[] {
  const out: ContactoUnificado[] = [];
  const vistos = new Set<string>();
  const push = (c: ContactoUnificado) => {
    const k = c.email.toLowerCase();
    if (!c.email.trim() || vistos.has(k)) return;
    vistos.add(k);
    out.push(c);
  };
  for (const p of proveedores) {
    if (!p.activo) continue;
    for (const c of p.contactos) {
      if (!c.activo || !c.email) continue;
      push({
        nombre: c.nombre,
        email: c.email,
        fuente: "proveedor",
        etiqueta: p.nombre,
        esPrincipal: c.es_principal,
        key: `prov-${c.id}`,
      });
    }
  }
  for (const c of contactosCliente) {
    if (!c.activo || !c.email) continue;
    push({
      nombre: c.nombre,
      email: c.email,
      fuente: "cliente",
      etiqueta: "Contacto del cliente",
      esPrincipal: c.principal,
      key: `cli-${c.id}`,
    });
  }
  for (const a of asesores) {
    if (!a.activo || !a.email) continue;
    push({
      nombre: a.nombre,
      email: a.email,
      fuente: "asesor",
      etiqueta: "Asesor comercial",
      esPrincipal: false,
      key: `ase-${a.id}`,
    });
  }
  // Equipo interno (soporte/admin) — para poner en copia a un compañero sin
  // teclear su correo. Va al final: si alguien ya salió como asesor, ese gana.
  for (const u of usuarios) {
    if (!u.email) continue;
    push({
      nombre: u.nombre,
      email: u.email,
      fuente: "interno",
      etiqueta: "Equipo interno",
      esPrincipal: false,
      key: `usr-${u.id}`,
    });
  }
  return out;
}

/**
 * Filtra y ordena por calidad de coincidencia. Sin query, devuelve la lista tal cual.
 * Puntaje: nombre empieza-con (100) > palabra del nombre empieza-con (80) >
 * substring en nombre (60) > substring en email (40) > substring en etiqueta (20).
 */
export function rankContactos(
  query: string,
  lista: ContactoUnificado[],
): ContactoUnificado[] {
  const q = norm(query);
  if (!q) return lista;
  const puntaje = (c: ContactoUnificado): number => {
    const nombre = norm(c.nombre);
    const email = norm(c.email);
    const etiqueta = norm(c.etiqueta);
    if (nombre.startsWith(q)) return 100;
    if (nombre.split(/\s+/).some((w) => w.startsWith(q))) return 80;
    if (nombre.includes(q)) return 60;
    if (email.includes(q)) return 40;
    if (etiqueta.includes(q)) return 20;
    return 0;
  };
  return lista
    .map((c) => ({ c, s: puntaje(c) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || a.c.nombre.localeCompare(b.c.nombre))
    .map((x) => x.c);
}
